import api from './api';
import errorHandler from '../utils/errorHandler';
import pusherService from './pusher';

class OrderTrackingService {
    constructor() {
        this.trackingSubscriptions = new Map();
        this.statusCallbacks = new Map();
        this.isInitialized = false;
    }

    // Initialize order tracking
    async initialize() {
        try {
            if (this.isInitialized) return true;

            // Initialize Pusher for real-time updates
            await pusherService.initialize();
            
            this.isInitialized = true;
            return true;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Order tracking initialization' });
            return false;
        }
    }

    // Start tracking an order
    async startTracking(orderId, callbacks = {}) {
        try {
            if (!this.isInitialized) {
                await this.initialize();
            }

            // Store callbacks for this order
            this.statusCallbacks.set(orderId, callbacks);

            // Subscribe to order updates via Pusher
            const channel = pusherService.subscribe(`order.${orderId}`);
            
            channel.bind('status.updated', (data) => {
                this.handleStatusUpdate(orderId, data);
            });

            channel.bind('location.updated', (data) => {
                this.handleLocationUpdate(orderId, data);
            });

            channel.bind('delivery.updated', (data) => {
                this.handleDeliveryUpdate(orderId, data);
            });

            // Store subscription
            this.trackingSubscriptions.set(orderId, channel);

            // Get initial order status
            const orderStatus = await this.getOrderStatus(orderId);
            callbacks.onStatusUpdate?.(orderStatus);

            return true;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Start order tracking' });
            return false;
        }
    }

    // Stop tracking an order
    stopTracking(orderId) {
        try {
            // Unsubscribe from Pusher channel
            const channel = this.trackingSubscriptions.get(orderId);
            if (channel) {
                pusherService.unsubscribe(`order.${orderId}`);
                this.trackingSubscriptions.delete(orderId);
            }

            // Remove callbacks
            this.statusCallbacks.delete(orderId);

            return true;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Stop order tracking' });
            return false;
        }
    }

    // Get order status
    async getOrderStatus(orderId) {
        try {
            const response = await api.get(`/orders/${orderId}/status`);
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to get order status');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order status' });
            throw error;
        }
    }

    // Get order details
    async getOrderDetails(orderId) {
        try {
            const response = await api.get(`/orders/${orderId}`);
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to get order details');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order details' });
            throw error;
        }
    }

    // Get order timeline
    async getOrderTimeline(orderId) {
        try {
            const response = await api.get(`/orders/${orderId}/timeline`);
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to get order timeline');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order timeline' });
            throw error;
        }
    }

    // Get delivery location
    async getDeliveryLocation(orderId) {
        try {
            const response = await api.get(`/orders/${orderId}/location`);
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to get delivery location');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get delivery location' });
            throw error;
        }
    }

    // Update delivery location
    async updateDeliveryLocation(orderId, location) {
        try {
            const response = await api.put(`/orders/${orderId}/location`, location);
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to update delivery location');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Update delivery location' });
            throw error;
        }
    }

    // Cancel order
    async cancelOrder(orderId, reason = '') {
        try {
            const response = await api.post(`/orders/${orderId}/cancel`, { reason });
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to cancel order');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Cancel order' });
            throw error;
        }
    }

    // Reschedule order
    async rescheduleOrder(orderId, newDateTime) {
        try {
            const response = await api.post(`/orders/${orderId}/reschedule`, { 
                new_date_time: newDateTime 
            });
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to reschedule order');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Reschedule order' });
            throw error;
        }
    }

    // Rate order
    async rateOrder(orderId, rating, review = '') {
        try {
            const response = await api.post(`/orders/${orderId}/rate`, { 
                rating, 
                review 
            });
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to rate order');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Rate order' });
            throw error;
        }
    }

    // Get order history
    async getOrderHistory(page = 1, limit = 10, status = null) {
        try {
            const params = { page, limit };
            if (status) params.status = status;

            const response = await api.get('/orders/history', { params });
            
            if (response.data.success) {
                return response.data.data;
            }

            throw new Error('Failed to get order history');
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get order history' });
            throw error;
        }
    }

    // Handle status update
    handleStatusUpdate(orderId, data) {
        const callbacks = this.statusCallbacks.get(orderId);
        if (callbacks) {
            callbacks.onStatusUpdate?.(data);
        }
    }

    // Handle location update
    handleLocationUpdate(orderId, data) {
        const callbacks = this.statusCallbacks.get(orderId);
        if (callbacks) {
            callbacks.onLocationUpdate?.(data);
        }
    }

    // Handle delivery update
    handleDeliveryUpdate(orderId, data) {
        const callbacks = this.statusCallbacks.get(orderId);
        if (callbacks) {
            callbacks.onDeliveryUpdate?.(data);
        }
    }

    // Get order status display info
    getStatusDisplayInfo(status) {
        const statusMap = {
            'pending': {
                label: 'Order Pending',
                description: 'Your order is being processed',
                color: 'orange',
                icon: 'clock-circle',
                progress: 10
            },
            'confirmed': {
                label: 'Order Confirmed',
                description: 'Your order has been confirmed',
                color: 'blue',
                icon: 'check-circle',
                progress: 25
            },
            'preparing': {
                label: 'Preparing Food',
                description: 'Your food is being prepared',
                color: 'purple',
                icon: 'fire',
                progress: 50
            },
            'ready': {
                label: 'Ready for Delivery',
                description: 'Your order is ready and will be delivered soon',
                color: 'cyan',
                icon: 'check-circle',
                progress: 75
            },
            'out_for_delivery': {
                label: 'Out for Delivery',
                description: 'Your order is on its way',
                color: 'green',
                icon: 'truck',
                progress: 90
            },
            'delivered': {
                label: 'Delivered',
                description: 'Your order has been delivered',
                color: 'green',
                icon: 'check-circle',
                progress: 100
            },
            'cancelled': {
                label: 'Cancelled',
                description: 'Your order has been cancelled',
                color: 'red',
                icon: 'close-circle',
                progress: 0
            },
            'refunded': {
                label: 'Refunded',
                description: 'Your order has been refunded',
                color: 'gray',
                icon: 'dollar-circle',
                progress: 0
            }
        };

        return statusMap[status] || {
            label: 'Unknown Status',
            description: 'Status information not available',
            color: 'gray',
            icon: 'question-circle',
            progress: 0
        };
    }

    // Get estimated delivery time
    getEstimatedDeliveryTime(orderStatus) {
        if (!orderStatus.estimated_delivery_time) {
            return null;
        }

        const now = new Date();
        const deliveryTime = new Date(orderStatus.estimated_delivery_time);
        const diffMs = deliveryTime - now;
        const diffMins = Math.ceil(diffMs / (1000 * 60));

        if (diffMins <= 0) {
            return 'Delivered';
        } else if (diffMins < 60) {
            return `${diffMins} minutes`;
        } else {
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            return `${hours}h ${mins}m`;
        }
    }

    // Get delivery progress percentage
    getDeliveryProgress(orderStatus) {
        const statusInfo = this.getStatusDisplayInfo(orderStatus.status);
        return statusInfo.progress;
    }

    // Check if order can be cancelled
    canCancelOrder(orderStatus) {
        const cancellableStatuses = ['pending', 'confirmed'];
        return cancellableStatuses.includes(orderStatus.status);
    }

    // Check if order can be rescheduled
    canRescheduleOrder(orderStatus) {
        const reschedulableStatuses = ['pending', 'confirmed', 'preparing'];
        return reschedulableStatuses.includes(orderStatus.status);
    }

    // Check if order can be rated
    canRateOrder(orderStatus) {
        return orderStatus.status === 'delivered' && !orderStatus.rating;
    }

    // Get order timeline events
    getTimelineEvents(timeline) {
        return timeline.map(event => ({
            ...event,
            displayInfo: this.getStatusDisplayInfo(event.status)
        }));
    }

    // Cleanup all tracking
    cleanup() {
        this.trackingSubscriptions.forEach((channel, orderId) => {
            this.stopTracking(orderId);
        });
        this.trackingSubscriptions.clear();
        this.statusCallbacks.clear();
    }
}

// Create singleton instance
const orderTrackingService = new OrderTrackingService();

export default orderTrackingService;
